import React, { type SyntheticEvent, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import {
	type UIAnalyticsEvent,
	useAnalyticsEvents as useAllAnalyticsEvents,
} from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { useConnectedTeams } from '@atlaskit/teams-public';
import { token } from '@atlaskit/tokens';

import PopupComponent from '../../common/no-background-popup-component';
import { TeamSelector } from '../team-selector';

import messages from './messages';

type TeamButtonProps = {
	containerId: string; // TODO: ARI type?
	Wrapper?: React.ElementType;
};

const styles = cssMap({
	button: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		paddingInline: token('space.075'),
		display: 'flex',
		alignItems: 'center',
		height: '32px',
		color: token('color.text.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

export const TeamButton = ({
	containerId,
	Wrapper = React.Fragment,
}: TeamButtonProps): React.JSX.Element | null => {
	const {
		numberOfTeams,
		teams,
		isLoading,
		hasLoaded,
		containerId: currentContainerId,
		fetchNumberOfConnectedTeams,
		fetchConnectedTeams,
	} = useConnectedTeams();
	// NOTE: Later add loading state when button shows always
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	const { fireEvent } = useAnalyticsEvents();
	const { createAnalyticsEvent } = useAllAnalyticsEvents();

	useEffect(() => {
		// if containerId is changed, we need to fetch the teams again
		if (currentContainerId === undefined || containerId !== currentContainerId) {
			fetchNumberOfConnectedTeams(containerId);
		}
	}, [containerId, fetchNumberOfConnectedTeams, currentContainerId]);

	useEffect(() => {
		if (containerId === currentContainerId && numberOfTeams !== undefined && numberOfTeams > 0) {
			const result = createAnalyticsEvent({})?.context.find(
				(item) => item.attributes && item.attributes.scope !== undefined,
			);

			fireEvent('ui.teamButton.viewed', {
				teamsCount: numberOfTeams ?? 0,
				scope: result?.attributes?.scope ?? '',
				version: 'm1',
			});
		}
	}, [containerId, createAnalyticsEvent, currentContainerId, fireEvent, numberOfTeams]);

	const onClick = useCallback(
		(_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			setIsOpen(true);

			const result = analyticsEvent?.context.find(
				(item) => item.attributes && item.attributes.scope !== undefined,
			);

			fireEvent('ui.teamButton.clicked', {
				teamsCount: numberOfTeams ?? 0,
				scope: result?.attributes?.scope ?? '',
				version: 'm1',
			});

			if (!teams && !isLoading && !hasLoaded) {
				fetchConnectedTeams(containerId);
			}
		},
		[containerId, fetchConnectedTeams, isLoading, teams, hasLoaded, fireEvent, numberOfTeams],
	);

	if (containerId !== currentContainerId || numberOfTeams === undefined || numberOfTeams === 0) {
		return null;
	}

	return (
		<Popup
			isOpen={isOpen}
			testId="team-button-popup"
			onClose={() => setIsOpen(false)}
			content={() => <TeamSelector containerId={containerId} teams={teams} />}
			trigger={(triggerProps: TriggerProps) => (
				<Wrapper>
					<Pressable
						{...triggerProps}
						onClick={onClick}
						xcss={styles.button}
						testId="team-button-test-id"
					>
						<TeamsIcon color="currentColor" label={formatMessage(messages.iconLabel)} />
						{numberOfTeams > 1 && <Box padding="space.050">{numberOfTeams}</Box>}
					</Pressable>
				</Wrapper>
			)}
			placement="bottom-start"
			strategy="absolute"
			popupComponent={PopupComponent}
			shouldRenderToParent={fg('should-render-to-parent-should-be-true-people-and-')}
		/>
	);
};
